import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { KGInstance } from '../../types';
import { extractErrorMessage } from '../../utils/errorHandler';
import LaunchInstancePanel from '../LaunchInstancePanel';
import TypedConfirmModal from '../TypedConfirmModal';
import InstanceLogs from './InstanceLogs';
import '../shared.css';
import './InstanceList.css';

const POLL_INTERVAL_MS = 10_000;

interface InstanceListProps {
  kgId: string;
  versionId: string;
  onRunningCount?: (count: number) => void;
}

function secondsSince(date: Date): number {
  return Math.floor((Date.now() - date.getTime()) / 1000);
}

function formatUptime(createdAt: string | number | undefined): string {
  if (!createdAt) return '—';
  const diff = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
  return `${Math.floor(diff / 86400)}d`;
}

function statusPillClass(status: string): string {
  switch (status?.toUpperCase()) {
    case 'RUNNING':
    case 'ACTIVE':       return 'status-pill--running';
    case 'STOPPED':      return 'status-pill--stopped';
    case 'TERMINATED':   return 'status-pill--terminated';
    case 'PROVISIONING':
    case 'PENDING':      return 'status-pill--provisioning';
    case 'STOPPING':     return 'status-pill--stopping';
    case 'ERROR':        return 'status-pill--error';
    default:             return 'status-pill--stopped';
  }
}

const InstanceList: React.FC<InstanceListProps> = ({ kgId, versionId, onRunningCount }) => {
  const navigate = useNavigate();
  const [instances, setInstances] = useState<KGInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [showLaunchPanel, setShowLaunchPanel] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<{ [key: string]: boolean }>({});
  const [showLogsForInstance, setShowLogsForInstance] = useState<string | null>(null);
  const [confirmTerminateId, setConfirmTerminateId] = useState<string | null>(null);
  const [lastPolledAt, setLastPolledAt] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [toastMsg, setToastMsg] = useState<{ text: string; undoFn?: () => void } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [copyToast, setCopyToast] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const copyId = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id).then(() => {
      setCopyToast(id);
      setTimeout(() => setCopyToast(null), 1800);
    });
  };

  // Update "X seconds ago" every second
  useEffect(() => {
    if (!lastPolledAt) return;
    const interval = setInterval(() => setSecondsAgo(secondsSince(lastPolledAt)), 1000);
    return () => clearInterval(interval);
  }, [lastPolledAt]);

  // Close overflow menu on outside click or scroll
  useEffect(() => {
    if (!openMenuId) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
        setMenuPos(null);
      }
    };
    const scrollHandler = () => { setOpenMenuId(null); setMenuPos(null); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('scroll', scrollHandler, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('scroll', scrollHandler, true);
    };
  }, [openMenuId]);

  const loadInstances = useCallback(async (showLoadingState: boolean = true) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    try {
      if (showLoadingState) setLoading(true);
      setError('');

      const response: any = await apiService.listInstances(kgId, versionId);
      if (signal.aborted) return;

      if (response.data) {
        const instanceList: KGInstance[] = response.data.map((item: any) => {
          // Fields may be at root or nested under attributes
          const a = item.attributes ?? item;
          return {
            instance_id:        a.instance_id        ?? item.id ?? item.instance_id,
            kg_id:              a.kg_id              ?? kgId,
            version_id:         a.version_id         ?? versionId,
            instance_type:      a.instance_type      ?? item.instance_type,
            status:             a.status             ?? item.status,
            is_terminated:      a.is_terminated      ?? item.is_terminated    ?? false,
            default_instance:   a.default_instance   ?? item.default_instance ?? false,
            created_at:         a.created_at         ?? item.created_at,
            org_id:             a.org_id             ?? item.org_id,
            status_response:    a.status_response    ?? item.status_response,
            created_by:         a.created_by         ?? item.created_by         ?? '',
            created_by_email:   a.created_by_email   ?? item.created_by_email   ?? '',
          };
        });

        setInstances(instanceList);
        setLastPolledAt(new Date());
        setSecondsAgo(0);

        const runningCount = instanceList.filter(
          (i) => i.status?.toLowerCase() === 'running' && !i.is_terminated
        ).length;
        onRunningCount?.(runningCount);

        const runningInstances = instanceList.filter(
          (i) => i.status?.toLowerCase() === 'running' && !i.is_terminated
        );
        await Promise.allSettled(
          runningInstances.map(async (instance) => {
            if (signal.aborted) return;
            try {
              const statusResponse: any = await apiService.getInstanceStatus(
                kgId, versionId, instance.instance_id
              );
              if (signal.aborted) return;
              if (statusResponse.data?.attributes?.status_response) {
                setInstances(prev =>
                  prev.map(inst =>
                    inst.instance_id === instance.instance_id
                      ? { ...inst, status_response: statusResponse.data.attributes.status_response }
                      : inst
                  )
                );
              }
            } catch (err) {
              if (!signal.aborted) console.error(`Error fetching status for ${instance.instance_id}:`, err);
            }
          })
        );
      }
    } catch (err: any) {
      if (signal.aborted) return;
      setError(extractErrorMessage(err, 'Failed to load instances'));
    } finally {
      if (!signal.aborted && showLoadingState) setLoading(false);
    }
  }, [kgId, versionId, onRunningCount]);

  useEffect(() => {
    loadInstances(true);
    const interval = setInterval(() => loadInstances(false), POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      abortRef.current?.abort();
    };
  }, [loadInstances]);

  const handleStatusChange = async (
    instanceId: string,
    desiredStatus: 'RUNNING' | 'STOPPED' | 'TERMINATED'
  ) => {
    setActionError('');
    try {
      setUpdatingStatus(prev => ({ ...prev, [instanceId]: true }));
      await apiService.updateInstanceStatus(kgId, versionId, instanceId, desiredStatus);
      setInstances(prev =>
        prev.map(inst =>
          inst.instance_id === instanceId
            ? { ...inst, status: desiredStatus.toLowerCase(), is_terminated: desiredStatus === 'TERMINATED' }
            : inst
        )
      );
      setTimeout(() => loadInstances(false), 2000);
    } catch (err: any) {
      setActionError(extractErrorMessage(err, 'Failed to update instance status'));
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [instanceId]: false }));
    }
  };

  const handleStop = async (instanceId: string) => {
    await handleStatusChange(instanceId, 'STOPPED');
    const undoFn = () => handleStatusChange(instanceId, 'RUNNING');
    setToastMsg({ text: 'Instance stopped', undoFn });
    setTimeout(() => setToastMsg(null), 5000);
  };

  const handleSetDefault = async (instanceId: string) => {
    setActionError('');
    try {
      setUpdatingStatus(prev => ({ ...prev, [instanceId]: true }));
      await apiService.setDefaultInstance(kgId, versionId, instanceId);
      setInstances(prev =>
        prev.map(inst => ({ ...inst, default_instance: inst.instance_id === instanceId }))
      );
    } catch (err: any) {
      setActionError(extractErrorMessage(err, 'Failed to set default instance'));
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [instanceId]: false }));
    }
  };

  const handleInstanceCreated = () => {
    setShowLaunchPanel(false);
    loadInstances();
  };

  const filteredInstances = instances.filter((instance) => {
    const s = searchTerm.toLowerCase();
    return (
      instance.instance_id.toLowerCase().includes(s) ||
      instance.instance_type?.toLowerCase().includes(s) ||
      instance.status?.toLowerCase().includes(s) ||
      instance.created_by?.toLowerCase().includes(s) ||
      instance.created_by_email?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="instance-list tab-root">
      <div className="tab-topbar">
        <div className="tab-topbar-left">
          <input
            type="text"
            className="tab-search"
            placeholder="Search instances…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {!loading && (
            <span className="tab-stat-pill">
              {filteredInstances.length} of {instances.length}
            </span>
          )}
          {lastPolledAt && (
            <span className="inst-poll-indicator">
              Updated {secondsAgo < 5 ? 'just now' : `${secondsAgo}s ago`}
            </span>
          )}
        </div>
        <div className="tab-topbar-right">
          <button className="tab-btn tab-btn--primary" onClick={() => setShowLaunchPanel(true)}>
            + Launch Instance
          </button>
        </div>
      </div>

      {error && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error}</span>
        </div>
      )}

      {actionError && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{actionError}</span>
          <button className="tab-banner-close" onClick={() => setActionError('')}>✕</button>
        </div>
      )}

      {loading ? (
        <div className="tab-loader">
          <span className="tab-spinner" />
        </div>
      ) : instances.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">⬡</div>
          <div className="tab-empty-title">No instances yet</div>
          <div className="tab-empty-body">Launch an instance to start querying this knowledge graph.</div>
        </div>
      ) : filteredInstances.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">⌕</div>
          <div className="tab-empty-title">No matches</div>
          <div className="tab-empty-body">No instances match "{searchTerm}"</div>
        </div>
      ) : (
        <div className="inst-table-wrapper sticky-thead">
          <table className="inst-table">
            <thead>
              <tr>
                <th>Instance ID</th>
                <th>Type</th>
                <th>Uptime</th>
                <th>DB Status</th>
                <th>Status</th>
                <th>Created By</th>
                <th>Actions</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {filteredInstances.map((instance) => (
                <tr key={instance.instance_id} className="inst-row">
                  {/* Instance ID */}
                  <td
                    className="inst-id-cell copy-cell"
                    onClick={(e) => copyId(instance.instance_id, e)}
                    title="Click to copy"
                  >
                    <span className="mono-sm">{instance.instance_id}</span>
                    {instance.default_instance && (
                      <span className="inst-default-tag">default</span>
                    )}
                    <span className="copy-hint">{copyToast === instance.instance_id ? '✓ copied' : 'copy'}</span>
                  </td>

                  {/* Type */}
                  <td>
                    <span className="instance-type-tag">{instance.instance_type || 'instance'}</span>
                  </td>

                  {/* Uptime */}
                  <td className="inst-uptime-cell mono-sm">
                    {formatUptime(instance.created_at)}
                  </td>

                  {/* DB Status */}
                  <td>
                    {updatingStatus[instance.instance_id] ? (
                      <span className="row-spinner" />
                    ) : instance.status?.toLowerCase() !== 'running' ? (
                      <span className="inst-db-inactive">—</span>
                    ) : !instance.status_response ? (
                      <span className="inst-db-checking">Checking…</span>
                    ) : instance.status_response.error ? (
                      <span className="inst-db-error" title={instance.status_response.error}>
                        Error
                      </span>
                    ) : instance.status_response.message ? (
                      <span className="inst-db-ok">{instance.status_response.message}</span>
                    ) : (
                      <span className="inst-db-inactive">—</span>
                    )}
                  </td>

                  {/* Status pill + default chip */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span className={`status-pill ${statusPillClass(instance.status)}`}>
                        {instance.status?.toUpperCase() || 'UNKNOWN'}
                      </span>
                      {instance.default_instance && (
                        <span className="inst-default-tag">default</span>
                      )}
                    </div>
                  </td>

                  {/* Created By */}
                  <td className="inst-created-by-cell">
                    <span className="inst-created-by" title={instance.created_by_email || instance.created_by || ''}>
                      {instance.created_by_email || instance.created_by || <span style={{ opacity: 0.4 }}>—</span>}
                    </span>
                  </td>

                  {/* Explicit Actions column */}
                  <td className="inst-actions-cell">
                    <button
                      className="inst-action-btn"
                      onClick={() => setShowLogsForInstance(instance.instance_id)}
                      title="View data import logs"
                    >
                      Data Import Logs
                    </button>
                    <button
                      className="inst-action-btn"
                      onClick={() => navigate(`/audit-log?kg=${kgId}&version=${versionId}&instance=${instance.instance_id}`)}
                      title="View query audit log for this instance"
                    >
                      Audit Log
                    </button>
                    {!instance.is_terminated && !instance.default_instance && (
                      <button
                        className="inst-action-btn inst-action-btn--default"
                        disabled={updatingStatus[instance.instance_id]}
                        onClick={() => handleSetDefault(instance.instance_id)}
                        title="Set as default instance"
                      >
                        Set default
                      </button>
                    )}
                    {!instance.is_terminated && instance.status?.toLowerCase() === 'running' && (
                      <button
                        className="inst-action-btn inst-action-btn--warn"
                        disabled={updatingStatus[instance.instance_id]}
                        onClick={() => handleStop(instance.instance_id)}
                        title="Stop instance"
                      >
                        Stop
                      </button>
                    )}
                    {!instance.is_terminated && instance.status?.toLowerCase() === 'stopped' && (
                      <button
                        className="inst-action-btn inst-action-btn--ok"
                        disabled={updatingStatus[instance.instance_id]}
                        onClick={() => handleStatusChange(instance.instance_id, 'RUNNING')}
                        title="Start instance"
                      >
                        Start
                      </button>
                    )}
                    {!instance.is_terminated && (
                      <button
                        className="inst-action-btn inst-action-btn--danger"
                        disabled={updatingStatus[instance.instance_id]}
                        onClick={() => setConfirmTerminateId(instance.instance_id)}
                        title="Terminate instance"
                      >
                        Terminate
                      </button>
                    )}
                  </td>

                  {/* Overflow menu (extra actions) */}
                  <td>
                    <div className="overflow-menu-wrap">
                      <button
                        className="overflow-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (openMenuId === instance.instance_id) {
                            setOpenMenuId(null);
                            setMenuPos(null);
                          } else {
                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            setOpenMenuId(instance.instance_id);
                          }
                        }}
                        aria-label="More actions"
                      >
                        ⋯
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Toast */}
      {toastMsg && (
        <div className="toast-container">
          <div className="toast">
            {toastMsg.text}
            {toastMsg.undoFn && (
              <button
                className="toast-action"
                onClick={() => { toastMsg.undoFn!(); setToastMsg(null); }}
              >
                Undo
              </button>
            )}
          </div>
        </div>
      )}

      {showLaunchPanel && (
        <LaunchInstancePanel
          kgId={kgId}
          versionId={versionId}
          onClose={() => setShowLaunchPanel(false)}
          onCreated={handleInstanceCreated}
        />
      )}

      {confirmTerminateId && (
        <TypedConfirmModal
          title="Terminate Instance"
          message="This will permanently terminate the instance. This action cannot be undone."
          confirmWord={confirmTerminateId}
          confirmLabel="Terminate"
          onConfirm={() => {
            const id = confirmTerminateId;
            setConfirmTerminateId(null);
            handleStatusChange(id, 'TERMINATED');
          }}
          onCancel={() => setConfirmTerminateId(null)}
        />
      )}

      {showLogsForInstance && (
        <InstanceLogs
          kgId={kgId}
          versionId={versionId}
          instanceId={showLogsForInstance}
          onClose={() => setShowLogsForInstance(null)}
        />
      )}

      {/* Fixed-position overflow menu — rendered outside the table so it's never clipped */}
      {openMenuId && menuPos && (() => {
        const instance = instances.find(i => i.instance_id === openMenuId);
        if (!instance) return null;
        return (
          <div
            ref={menuRef}
            className="overflow-menu"
            style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, left: 'auto', zIndex: 9999 }}
          >
            {!instance.default_instance && !instance.is_terminated && (
              <button
                className="overflow-menu-item"
                disabled={updatingStatus[instance.instance_id]}
                onClick={() => { setOpenMenuId(null); setMenuPos(null); handleSetDefault(instance.instance_id); }}
              >
                Set default
              </button>
            )}
            <button
              className="overflow-menu-item"
              onClick={() => { setOpenMenuId(null); setMenuPos(null); copyId(instance.instance_id, { stopPropagation: () => {} } as any); }}
            >
              Copy ID
            </button>
          </div>
        );
      })()}
    </div>
  );
};

export default InstanceList;
